// Safe-outbound-fetch URL guard (szzt-spec.md §9.1, threat A.4 #1).
//
// A gateway MUST restrict outbound fetches (resolution, retrieval, push handling) to public HTTPS
// endpoints and refuse private, loopback, and link-local ranges. This is the URL-SHAPE half of that
// control: it rejects non-HTTPS schemes and hostnames that are literal private/loopback/link-local
// IPs (or `localhost`). It cannot see the resolved IP of a DNS name — so the fetch layer MUST also
// pin resolved PDS endpoints and refuse to follow redirects to private targets (DNS-rebinding and
// redirect defenses, §9.1). Use both together; neither alone is sufficient.

import { GatewayError } from "./types.ts";

/** Parse a dotted-quad IPv4 literal to its four octets, or `null` if it is not one. */
function parseIPv4(host: string): [number, number, number, number] | null {
  const parts = host.split(".");
  if (parts.length !== 4) return null;
  const octets: number[] = [];
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    const n = Number(part);
    if (n > 255) return null;
    octets.push(n);
  }
  return octets as [number, number, number, number];
}

/** True for IPv4 loopback / private / link-local / unspecified ranges (§9.1). */
function isPrivateIPv4(o: [number, number, number, number]): boolean {
  const [a, b] = o;
  if (a === 0) return true; // 0.0.0.0/8 "this network" (incl. unspecified)
  if (a === 10) return true; // 10.0.0.0/8 private
  if (a === 127) return true; // 127.0.0.0/8 loopback
  if (a === 169 && b === 254) return true; // 169.254.0.0/16 link-local
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12 private
  if (a === 192 && b === 168) return true; // 192.168.0.0/16 private
  if (a === 100 && b >= 64 && b <= 127) return true; // 100.64.0.0/10 CGNAT
  return false;
}

/** True for IPv6 loopback / unspecified / unique-local (fc00::/7) / link-local (fe80::/10) (§9.1). */
function isPrivateIPv6(host: string): boolean {
  const h = host.toLowerCase();
  if (h === "::1" || h === "::") return true; // loopback / unspecified
  // IPv4-mapped/embedded (::ffff:127.0.0.1, ::ffff:10.0.0.1) — extract the trailing IPv4.
  const tail = h.split(":").pop();
  if (tail !== undefined && tail.includes(".")) {
    const v4 = parseIPv4(tail);
    if (v4 && isPrivateIPv4(v4)) return true;
  }
  // URL canonicalization renders IPv4-mapped addresses as hex (::ffff:7f00:1), not dotted
  // decimal. Classify their embedded IPv4 too; otherwise URL parsing bypasses the check above.
  const mapped = /^::ffff:([0-9a-f]{1,4}):([0-9a-f]{1,4})$/.exec(h);
  if (mapped) {
    const high = parseInt(mapped[1]!, 16);
    const low = parseInt(mapped[2]!, 16);
    if (isPrivateIPv4([high >>> 8, high & 255, low >>> 8, low & 255])) return true;
  }
  const first = h.split(":")[0] ?? "";
  if (/^f[cd][0-9a-f]{0,2}$/.test(first)) return true; // fc00::/7 unique-local (fc.. / fd..)
  if (/^fe[89ab][0-9a-f]?$/.test(first)) return true; // fe80::/10 link-local
  return false;
}

/** True when a host (hostname or IP literal) is private, loopback, or link-local — i.e. must be refused. */
export function isPrivateHost(host: string): boolean {
  const h = host.toLowerCase().replace(/\.$/, "");
  if (h === "" || h === "localhost" || h.endsWith(".localhost")) return true;
  const v4 = parseIPv4(h);
  if (v4) return isPrivateIPv4(v4);
  if (h.includes(":")) return isPrivateIPv6(h);
  return false;
}

/**
 * Assert a URL is a public HTTPS endpoint (§9.1). Throws {@link GatewayError} for a non-HTTPS scheme
 * or a private/loopback/link-local host. Bracketed IPv6 authorities are unwrapped before checking.
 */
export function assertPublicHttps(url: string): void {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    throw new GatewayError(`not a valid URL: ${url}`);
  }
  if (u.protocol !== "https:") {
    throw new GatewayError(`non-HTTPS URL refused (§9.1): ${url}`);
  }
  const host = u.hostname;
  const bare = host.startsWith("[") && host.endsWith("]") ? host.slice(1, -1) : host;
  if (isPrivateHost(bare)) {
    throw new GatewayError(`private/loopback/link-local host refused (§9.1): ${host}`);
  }
}

/** Non-throwing form of {@link assertPublicHttps}. */
export function isPublicHttpsUrl(url: string): boolean {
  try {
    assertPublicHttps(url);
    return true;
  } catch {
    return false;
  }
}
